import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { 
  Search,
  Building2,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Home,
  TrendingUp,
  TrendingDown,
  PoundSterling,
  Factory,
  FileText,
  ChevronRight,
  ChevronDown,
  Info,
  Check,
  Zap,
  BarChart3,
  Building,
  SlidersHorizontal,
  Save,
  RotateCcw,
  Users,
  Calendar,
  Download,
  FileSpreadsheet
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer, 
} from "recharts";
import { 
  getCreditPolicySummary, 
  getCreditPolicyLegalCases,
  getCreditPolicyLoanBook, 
  getCreditPolicyHomeownerAnalysis,
  getCreditPolicyIndustryComparison,
  fuzzySearchCompany,
  matchCompanyToCase,
  autoMatchCompanies
} from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "£0";
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const formatCurrencyShort = (value) => {
  if (!value) return "£0";
  if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `£${(value / 1000).toFixed(0)}k`;
  return `£${value.toFixed(0)}`;
};

export default function CreditPolicyIntelligence() {
  // Main data state
  const [loading, setLoading] = useState(true);
  const [legalCases, setLegalCases] = useState([]);
  const [loanBook, setLoanBook] = useState(null);
  const [homeownerAnalysis, setHomeownerAnalysis] = useState(null);
  const [summary, setSummary] = useState(null);
  const [industryComparison, setIndustryComparison] = useState(null);
  
  // Company matching state
  const [selectedCase, setSelectedCase] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [savingMatch, setSavingMatch] = useState(false);
  
  // Filter state
  const [filterStatus, setFilterStatus] = useState("unmatched");
  const [activeTab, setActiveTab] = useState("performance");
  
  // Auto-match state
  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [autoMatchResults, setAutoMatchResults] = useState(null);
  
  // Loan book matching state
  const [loanBookAutoMatchLoading, setLoanBookAutoMatchLoading] = useState(false);
  const [loanBookAutoMatchResults, setLoanBookAutoMatchResults] = useState(null);
  const [loanBookCases, setLoanBookCases] = useState([]);
  const [loanBookSortBy, setLoanBookSortBy] = useState("name");
  const [loanBookSortDir, setLoanBookSortDir] = useState("asc");
  const [selectedLoanBookCase, setSelectedLoanBookCase] = useState(null);
  const [loanBookMatchDialogOpen, setLoanBookMatchDialogOpen] = useState(false);
  
  // Performance Analysis state
  const [performanceAnalysis, setPerformanceAnalysis] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [expandedSectors, setExpandedSectors] = useState(new Set());
  const [sectorFilter, setSectorFilter] = useState('');
  
  // Credit Policy Settings state
  const [sectorRiskSummary, setSectorRiskSummary] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [editedLimits, setEditedLimits] = useState({});
  const [editedSubcatLimits, setEditedSubcatLimits] = useState({});
  const [riskTierDefaults, setRiskTierDefaults] = useState({ LOW: 200, MEDIUM: 100, HIGH: 50 });
  
  // Policy Simulation state
  const [simulationData, setSimulationData] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [customLimits, setCustomLimits] = useState({ HIGH: 50, MEDIUM: 100, LOW: 100 });
  const [excludedSubCategories, setExcludedSubCategories] = useState(new Set());

  // Deal Flow Analysis state
  const [dealFlowAnalysis, setDealFlowAnalysis] = useState(null);
  const [dealFlowLoading, setDealFlowLoading] = useState(false);
  const [dealFlowMatching, setDealFlowMatching] = useState(false);
  const [dealFlowMonths, setDealFlowMonths] = useState(6);
  const [dealFlowExclusions, setDealFlowExclusions] = useState(new Set());
  const [dealFlowSimulation, setDealFlowSimulation] = useState(null);
  const [dealFlowCacheStats, setDealFlowCacheStats] = useState(null);

  // Toggle sector expansion (used by both Performance and Scenario Builder)
  const toggleSectorExpand = (sector) => {
    setExpandedSectors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sector)) {
        newSet.delete(sector);
      } else {
        newSet.add(sector);
      }
      return newSet;
    });
  };

  // Expand all sectors in Scenario Builder
  const expandAllSectors = () => {
    if (simulationData?.sub_categories) {
      const allSectors = new Set(simulationData.sub_categories.map(s => s.sector));
      setExpandedSectors(allSectors);
    }
  };

  // Collapse all sectors
  const collapseAllSectors = () => {
    setExpandedSectors(new Set());
  };

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [summaryData, loanBookData, hoData, industryData] = await Promise.all([
        getCreditPolicySummary(),
        getCreditPolicyLoanBook(),
        getCreditPolicyHomeownerAnalysis(),
        getCreditPolicyIndustryComparison()
      ]);
      
      if (summaryData.success) setSummary(summaryData);
      if (loanBookData.success) setLoanBook(loanBookData);
      if (hoData.success) setHomeownerAnalysis(hoData);
      if (industryData.success) setIndustryComparison(industryData);
      
      // Fetch legal cases
      const casesData = await getCreditPolicyLegalCases();
      if (casesData.success) setLegalCases(casesData.cases || []);
      
      // Fetch loan book cases for matching
      try {
        const { data: loanBookCasesData } = await api.get(
          '/legal/credit-policy/loan-book-unmatched',
          { params: { limit: 200 } }
        );
        if (loanBookCasesData.success) setLoanBookCases(loanBookCasesData.loans || []);
      } catch (err) {
        console.error("Failed to load loan book cases:", err);
      }
      
    } catch (error) {
      console.error("Failed to load credit policy data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load performance analysis when tab changes
  useEffect(() => {
    if (activeTab === 'performance') {
      loadPerformanceAnalysis();
    }
  }, [activeTab]);

  // Load performance analysis
  const loadPerformanceAnalysis = async () => {
    try {
      setPerformanceLoading(true);
      const { data } = await api.get('/legal/ai-industry-enrichment/performance-analysis');
      if (data.success) setPerformanceAnalysis(data);
    } catch (error) {
      console.error("Failed to load performance analysis:", error);
    } finally {
      setPerformanceLoading(false);
    }
  };

  // Load credit policy settings
  const loadPolicySettings = async () => {
    try {
      setPolicyLoading(true);
      const { data } = await api.get('/legal/credit-policy/sector-risk-summary');
      if (data.success) {
        setSectorRiskSummary(data);
        setRiskTierDefaults(data.risk_tier_defaults || { LOW: 200, MEDIUM: 100, HIGH: 50 });
        const limits = {};
        data.sectors?.forEach(s => { if (s.is_custom) limits[s.sector] = s.affordability_limit; });
        setEditedLimits(limits);
        const subcatLimits = {};
        data.subcategories?.forEach(s => { if (s.is_custom) subcatLimits[s.sub_category] = s.affordability_limit; });
        setEditedSubcatLimits(subcatLimits);
      }
    } catch (error) {
      console.error("Failed to load policy settings:", error);
    } finally {
      setPolicyLoading(false);
    }
  };

  // Save credit policy settings
  const savePolicySettings = async () => {
    try {
      setSavingPolicy(true);
      const settings = {
        sector_limits: editedLimits,
        subcategory_limits: editedSubcatLimits,
        risk_tier_defaults: riskTierDefaults,
        default_limit: 100
      };
      
      const { data } = await api.post('/legal/credit-policy/settings', settings);
      if (data.success) {
        toast.success("Credit policy settings saved!");
        loadPolicySettings();
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      console.error("Failed to save policy settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingPolicy(false);
    }
  };

  // Load policy simulation
  const loadSimulation = async (limits = null, exclusions = null) => {
    try {
      setSimulationLoading(true);

      const body = {
        high_risk_limit: limits?.HIGH || customLimits.HIGH,
        medium_risk_limit: limits?.MEDIUM || customLimits.MEDIUM,
        low_risk_limit: limits?.LOW || customLimits.LOW,
        excluded_sub_categories: exclusions ? Array.from(exclusions) : Array.from(excludedSubCategories)
      };
      
      const { data } = await api.post('/legal/credit-policy/simulation-actual', body);
      if (data.success) {
        setSimulationData(data.simulation);
      }
    } catch (error) {
      console.error("Failed to load simulation:", error);
      toast.error("Failed to load simulation");
    } finally {
      setSimulationLoading(false);
    }
  };

  // Run custom simulation
  const runCustomSimulation = () => {
    loadSimulation(customLimits, excludedSubCategories);
  };
  
  // Toggle sub-category exclusion
  const toggleExclusion = (subCatName) => {
    setExcludedSubCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subCatName)) {
        newSet.delete(subCatName);
      } else {
        newSet.add(subCatName);
      }
      return newSet;
    });
  };
  
  // Exclude all HIGH risk sub-categories
  const excludeAllHighRisk = () => {
    if (simulationData?.sub_categories) {
      const highRiskSubCats = simulationData.sub_categories
        .filter(s => s.risk_tier === 'HIGH')
        .map(s => s.sub_category);
      setExcludedSubCategories(new Set(highRiskSubCats));
    }
  };
  
  // Clear all exclusions
  const clearExclusions = () => {
    setExcludedSubCategories(new Set());
  };

  // Load policy settings when tab changes to policy
  useEffect(() => {
    if (activeTab === 'policy') {
      loadPolicySettings();
      loadSimulation();
    }
  }, [activeTab]);

  // Load deal flow when tab changes
  useEffect(() => {
    if (activeTab === 'dealflow') {
      loadDealFlowAnalysis();
      loadDealFlowCacheStats();
    }
  }, [activeTab, dealFlowMonths]);

  // Load deal flow analysis
  const loadDealFlowAnalysis = async () => {
    try {
      setDealFlowLoading(true);
      const { data } = await api.get('/legal/deal-flow/analysis', {
        params: { months_back: dealFlowMonths }
      });
      if (data.success) {
        setDealFlowAnalysis(data.analysis);
      } else {
        toast.error(data.error || "Failed to load deal flow analysis");
      }
    } catch (error) {
      console.error("Failed to load deal flow:", error);
      toast.error("Failed to load deal flow analysis");
    } finally {
      setDealFlowLoading(false);
    }
  };

  // Load deal flow cache stats
  const loadDealFlowCacheStats = async () => {
    try {
      const { data } = await api.get('/legal/deal-flow/cache-stats');
      if (data.success) {
        setDealFlowCacheStats(data.cache_stats);
      }
    } catch (error) {
      console.error("Failed to load cache stats:", error);
    }
  };

  // Run Companies House matching for deal flow
  const runDealFlowMatching = async () => {
    try {
      setDealFlowMatching(true);
      toast.info("Starting Companies House matching (this may take a few minutes)...");
      const { data } = await api.post('/legal/deal-flow/match-companies', null, {
        params: { months_back: dealFlowMonths, max_matches: 600 }
      });
      if (data.success) {
        toast.success(`Matched ${data.matching_summary.new_matches} new companies!`);
        await loadDealFlowAnalysis();
        await loadDealFlowCacheStats();
      } else {
        toast.error(data.error || "Matching failed");
      }
    } catch (error) {
      console.error("Failed to match companies:", error);
      toast.error("Failed to match companies");
    } finally {
      setDealFlowMatching(false);
    }
  };

  // Simulate deal flow exclusion
  const simulateDealFlowExclusion = async () => {
    if (dealFlowExclusions.size === 0) {
      setDealFlowSimulation(null);
      return;
    }
    try {
      const { data } = await api.post(
        '/legal/deal-flow/simulate-exclusion',
        { excluded_industries: Array.from(dealFlowExclusions) },
        { params: { months_back: dealFlowMonths } }
      );
      if (data.success) {
        setDealFlowSimulation(data.simulation);
      }
    } catch (error) {
      console.error("Failed to simulate exclusion:", error);
    }
  };

  // Toggle industry exclusion in deal flow
  const toggleDealFlowExclusion = (industry) => {
    setDealFlowExclusions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(industry)) {
        newSet.delete(industry);
      } else {
        newSet.add(industry);
      }
      return newSet;
    });
  };

  // Recalculate simulation when exclusions change
  useEffect(() => {
    if (activeTab === 'dealflow' && dealFlowAnalysis) {
      simulateDealFlowExclusion();
    }
  }, [dealFlowExclusions]);

  // Export deal flow to CSV
  const exportDealFlowCSV = async () => {
    try {
      toast.info("Generating CSV export...");
      const { data: blob } = await api.post(
        '/legal/deal-flow/export/csv',
        { excluded_industries: Array.from(dealFlowExclusions) },
        { params: { months_back: dealFlowMonths }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deal_flow_analysis_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("CSV exported successfully!");
    } catch (error) {
      console.error("CSV export error:", error);
      toast.error("Failed to export CSV");
    }
  };

  // Export deal flow to PDF
  const exportDealFlowPDF = async () => {
    try {
      toast.info("Generating PDF report...");
      const { data: blob } = await api.post(
        '/legal/deal-flow/export/pdf',
        { excluded_industries: Array.from(dealFlowExclusions) },
        { params: { months_back: dealFlowMonths }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deal_flow_analysis_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
  };

  // Export Policy Simulation to CSV - ALL industries grouped by sector
  const exportSimulationCSV = async () => {
    try {
      toast.info("Fetching all industries for export...");
      
      // Fetch ALL industries from the new endpoint
      const { data } = await api.get('/legal/credit-policy/all-industries', {
        params: {
          high_risk_limit: customLimits.HIGH,
          medium_risk_limit: customLimits.MEDIUM,
          low_risk_limit: customLimits.LOW
        }
      });
      if (!data.success) {
        toast.error("Failed to fetch industries");
        return;
      }

      const rows = [
        ["Sector / Sub-Category", "Risk Band", "Funding Limit (% Monthly Sales)", "Default Rate", "Status"]
      ];

      // Group by sector
      const bySector = {};
      data.industries.forEach(item => {
        const sector = item.sector || "Unknown";
        if (!bySector[sector]) bySector[sector] = [];
        bySector[sector].push(item);
      });

      // Sort sectors and add to rows with grouping
      Object.keys(bySector).sort().forEach(sector => {
        // Add sector header row
        rows.push([`=== ${sector.toUpperCase()} ===`, "", "", "", ""]);
        
        // Add industries within sector
        bySector[sector]
          .sort((a, b) => (a.sub_category || "").localeCompare(b.sub_category || ""))
          .forEach(item => {
            const isExcluded = excludedSubCategories.has(item.sub_category);
            rows.push([
              `    ${item.sub_category || "Unknown"}`,
              item.risk_tier || "LOW",
              isExcluded ? "EXCLUDED" : `${item.funding_limit}%`,
              `${item.default_rate || 0}%`,
              isExcluded ? "EXCLUDED" : "ACTIVE"
            ]);
          });
        
        // Add empty row after sector
        rows.push([]);
      });

      // Policy Summary
      rows.push(["POLICY SETTINGS"]);
      rows.push(["HIGH Risk Limit (>15% default)", `${customLimits.HIGH}% of monthly sales`]);
      rows.push(["MEDIUM Risk Limit (8-15% default)", `${customLimits.MEDIUM}% of monthly sales`]);
      rows.push(["LOW Risk Limit (<8% default)", `${customLimits.LOW}% of monthly sales`]);
      rows.push([]);
      rows.push(["SUMMARY"]);
      rows.push(["Total Industries", data.total_industries]);
      rows.push(["Total Sectors", Object.keys(bySector).length]);
      rows.push(["HIGH Risk Industries", data.risk_breakdown.HIGH]);
      rows.push(["MEDIUM Risk Industries", data.risk_breakdown.MEDIUM]);
      rows.push(["LOW Risk Industries", data.risk_breakdown.LOW]);
      rows.push(["Excluded Industries", excludedSubCategories.size]);
      rows.push(["Generated", new Date().toLocaleString("en-GB")]);

      const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credit_policy_by_sector_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(`Exported ${data.total_industries} industries in ${Object.keys(bySector).length} sectors!`);
    } catch (error) {
      console.error("CSV export error:", error);
      toast.error("Failed to export CSV");
    }
  };

  // Export Policy Simulation to PDF - ALL industries
  const exportSimulationPDF = async () => {
    try {
      toast.info("Generating PDF report with all industries...");
      
      // Fetch ALL industries from the new endpoint
      const { data } = await api.get('/legal/credit-policy/all-industries', {
        params: {
          high_risk_limit: customLimits.HIGH,
          medium_risk_limit: customLimits.MEDIUM,
          low_risk_limit: customLimits.LOW
        }
      });
      if (!data.success) {
        toast.error("Failed to fetch industries");
        return;
      }

      // Map industries with exclusion status
      const industries = data.industries.map(item => ({
        sub_category: item.sub_category,
        sector: item.sector,
        risk_tier: item.risk_tier,
        funding_limit: excludedSubCategories.has(item.sub_category) ? 0 : item.funding_limit,
        default_rate: item.default_rate || 0,
        is_excluded: excludedSubCategories.has(item.sub_category)
      }));

      // Send to PDF endpoint
      const { data: blob } = await api.post(
        '/legal/credit-policy/simulation/export/pdf',
        {
          sub_categories: industries,
          policy_limits: customLimits,
          excluded_count: excludedSubCategories.size,
          total_count: data.total_industries
        },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credit_policy_all_industries_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(`Exported ${data.total_industries} industries to PDF!`);
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
  };

  // Search Companies House
  const handleSearchCompany = async (companyName) => {
    if (!companyName || companyName.length < 3) {
      toast.warning("Please enter at least 3 characters to search");
      return;
    }
    try {
      setSearchLoading(true);
      setSearchResults([]);
      const result = await fuzzySearchCompany(companyName, 10);
      if (result.success) {
        setSearchResults(result.results || []);
        if (result.results?.length === 0) toast.info("No companies found matching that name");
      } else {
        toast.error("Search failed");
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search Companies House");
    } finally {
      setSearchLoading(false);
    }
  };

  // Open match dialog
  const openMatchDialog = (legalCase) => {
    setSelectedCase(legalCase);
    setSearchResults([]);
    setMatchDialogOpen(true);
    if (legalCase.company_name) handleSearchCompany(legalCase.company_name);
  };

  // Save company match
  const handleSaveMatch = async (companyNumber, companyData) => {
    if (!selectedCase?.id || !companyNumber) {
      toast.error("Missing case or company data");
      return;
    }
    try {
      setSavingMatch(true);
      const result = await matchCompanyToCase(selectedCase.id, companyNumber, 'legal');
      if (result.success) {
        toast.success(`Matched ${selectedCase.company_name} → ${result.matched_company}`);
        setLegalCases(prev => prev.map(c => 
          c.id === selectedCase.id 
            ? { ...c, company_number: companyNumber, sic_code: result.sic_code, industry_description: result.industry_description, matched: true }
            : c
        ));
        setMatchDialogOpen(false);
        setSelectedCase(null);
        setSearchResults([]);
      } else {
        toast.error(result.error || "Failed to save match");
      }
    } catch (error) {
      console.error("Save match error:", error);
      toast.error("Failed to save match");
    } finally {
      setSavingMatch(false);
    }
  };

  // Save loan book match
  const saveLoanBookMatch = async (companyNumber) => {
    if (!selectedLoanBookCase || !companyNumber) return;
    try {
      setSavingMatch(true);
      const result = await matchCompanyToCase(selectedLoanBookCase.id, companyNumber, 'loanbook');
      if (result.success) {
        toast.success(`Matched ${selectedLoanBookCase.company_name} → ${result.matched_company}`);
        setLoanBookCases(prev => prev.map(c => 
          c.id === selectedLoanBookCase.id 
            ? { ...c, company_number: companyNumber, sic_code: result.sic_code, industry_description: result.industry_description, matched: true }
            : c
        ));
        setLoanBookMatchDialogOpen(false);
        setSelectedLoanBookCase(null);
        setSearchResults([]);
        await loadData();
      } else {
        toast.error(result.error || "Failed to save match");
      }
    } catch (error) {
      console.error("Save loan book match error:", error);
      toast.error("Failed to save match");
    } finally {
      setSavingMatch(false);
    }
  };

  // Auto-match
  const handleAutoMatch = async () => {
    try {
      setAutoMatchLoading(true);
      setAutoMatchResults(null);
      toast.info("Starting auto-match process...");
      const result = await autoMatchCompanies('legal', 0.95, 50);
      if (result.success) {
        setAutoMatchResults(result);
        if (result.auto_matched > 0) {
          toast.success(`Auto-matched ${result.auto_matched} cases!`);
          await loadData();
        } else {
          toast.info("No high-confidence matches found");
        }
      } else {
        toast.error("Auto-match failed");
      }
    } catch (error) {
      console.error("Auto-match error:", error);
      toast.error("Auto-match failed");
    } finally {
      setAutoMatchLoading(false);
    }
  };

  // Auto-match loan book
  const handleLoanBookAutoMatch = async (minScore = 0.88) => {
    try {
      setLoanBookAutoMatchLoading(true);
      setLoanBookAutoMatchResults(null);
      toast.info("Starting loan book auto-match...");
      const result = await autoMatchCompanies('loanbook', minScore, 100);
      if (result.success) {
        setLoanBookAutoMatchResults(result);
        if (result.auto_matched > 0) {
          toast.success(`Auto-matched ${result.auto_matched} loan book companies!`);
          await loadData();
        } else {
          toast.info("No more high-confidence matches found");
        }
      } else {
        toast.error("Auto-match failed");
      }
    } catch (error) {
      console.error("Loan book auto-match error:", error);
      toast.error("Auto-match failed");
    } finally {
      setLoanBookAutoMatchLoading(false);
    }
  };

  // Filter cases
  const filteredCases = legalCases.filter(c => {
    if (filterStatus === "unmatched") return !c.sic_code && !c.company_number;
    if (filterStatus === "matched") return c.sic_code || c.company_number;
    return true;
  });

  // Calculate stats
  const matchedCount = legalCases.filter(c => c.sic_code || c.company_number).length;
  const unmatchedCount = legalCases.length - matchedCount;
  const matchProgress = legalCases.length > 0 ? (matchedCount / legalCases.length) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[600px]" data-testid="loading-state">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600" />
          <p className="mt-4 text-muted-foreground">Loading credit policy data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="credit-policy-intelligence">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Credit Policy Intelligence</h1>
          <p className="text-muted-foreground">Analyze loan performance to shape credit policy</p>
        </div>
        <Button onClick={loadData} variant="outline" className="gap-2" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-5">
            <p className="text-xs text-red-600 uppercase font-medium">Legal Cases</p>
            <p className="text-2xl font-bold text-red-700">{legalCases.length}</p>
            <p className="text-xs text-red-600">{formatCurrencyShort(summary?.summary?.total_legal_principal)} at risk</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-5">
            <p className="text-xs text-blue-600 uppercase font-medium">Loan Book</p>
            <p className="text-2xl font-bold text-blue-700">{summary?.summary?.total_loans || 0}</p>
            <p className="text-xs text-blue-600">{formatCurrencyShort(summary?.summary?.total_book_principal)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-5">
            <p className="text-xs text-amber-600 uppercase font-medium">Default Rate</p>
            <p className="text-2xl font-bold text-amber-700">{performanceAnalysis?.summary?.overall_default_rate || summary?.summary?.overall_default_rate_pct || 0}%</p>
            <p className="text-xs text-amber-600">of loans go legal</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-5">
            <p className="text-xs text-green-600 uppercase font-medium">Matched Cases</p>
            <p className="text-2xl font-bold text-green-700">{matchedCount}</p>
            <p className="text-xs text-green-600">{unmatchedCount} remaining</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-5">
            <p className="text-xs text-purple-600 uppercase font-medium">Match Progress</p>
            <p className="text-2xl font-bold text-purple-700">{matchProgress.toFixed(0)}%</p>
            <Progress value={matchProgress} className="mt-2 h-2" />
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="performance" className="gap-2" data-testid="tab-performance">
            <BarChart3 className="w-4 h-4" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="policy" className="gap-2" data-testid="tab-policy">
            <SlidersHorizontal className="w-4 h-4" />
            Policy Simulation
          </TabsTrigger>
          <TabsTrigger value="matching" className="gap-2" data-testid="tab-matching">
            <Building2 className="w-4 h-4" />
            Matching
            {unmatchedCount > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs">{unmatchedCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="homeowner" className="gap-2" data-testid="tab-homeowner">
            <Home className="w-4 h-4" />
            HO Analysis
          </TabsTrigger>
        </TabsList>

        {/* Performance Tab */}
        <TabsContent value="performance" className="mt-6 space-y-6" data-testid="performance-tab">
          {performanceLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
              <p className="text-muted-foreground mt-2">Loading industry performance analysis...</p>
            </div>
          ) : performanceAnalysis?.summary ? (
            <>
              {/* Key Metrics + HO vs Non-HO Comparison */}
              <div className="grid grid-cols-12 gap-4">
                {/* Left side: Key metrics */}
                <div className="col-span-7 grid grid-cols-4 gap-3">
                  <Card className="bg-gradient-to-br from-slate-700 to-slate-800 text-white">
                    <CardContent className="pt-4 pb-3">
                      <p className="text-[10px] opacity-70 uppercase tracking-wide">Total Book</p>
                      <p className="text-2xl font-bold">{performanceAnalysis.summary.total_loans?.toLocaleString()}</p>
                      <p className="text-xs opacity-60">£{(performanceAnalysis.summary.total_principal / 1000000).toFixed(1)}M</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-red-600 to-red-700 text-white">
                    <CardContent className="pt-4 pb-3">
                      <p className="text-[10px] opacity-70 uppercase tracking-wide">Default Rate</p>
                      <p className="text-2xl font-bold">{performanceAnalysis.summary.overall_default_rate}%</p>
                      <p className="text-xs opacity-60">{performanceAnalysis.summary.total_defaults} defaults</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                    <CardContent className="pt-4 pb-3">
                      <p className="text-[10px] opacity-70 uppercase tracking-wide">Bad Debt</p>
                      <p className="text-2xl font-bold">£{(performanceAnalysis.summary.total_default_principal / 1000000).toFixed(1)}M</p>
                      <p className="text-xs opacity-60">{((performanceAnalysis.summary.total_default_principal / performanceAnalysis.summary.total_principal) * 100).toFixed(0)}% of book</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
                    <CardContent className="pt-4 pb-3">
                      <p className="text-[10px] opacity-70 uppercase tracking-wide">Sectors</p>
                      <p className="text-2xl font-bold">{performanceAnalysis.summary.sectors_analyzed}</p>
                      <p className="text-xs opacity-60">{performanceAnalysis.summary.enrichment_coverage} companies</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Right side: HO vs Non-HO comparison */}
                <div className="col-span-5">
                  <Card className="h-full border-2 border-slate-200">
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1">
                          <Users className="w-3 h-3" /> HO vs Non-HO Comparison
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-xs text-blue-600 font-medium">Homeowner (HO)</p>
                          <p className="text-xl font-bold text-blue-700">{performanceAnalysis.summary.ho_default_rate}%</p>
                          <p className="text-[10px] text-blue-500">{performanceAnalysis.summary.ho_loans} loans</p>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                          <p className="text-xs text-orange-600 font-medium">Non-Homeowner</p>
                          <p className="text-xl font-bold text-orange-700">{performanceAnalysis.summary.nonho_default_rate}%</p>
                          <p className="text-[10px] text-orange-500">{performanceAnalysis.summary.nonho_loans} loans</p>
                        </div>
                      </div>
                      {performanceAnalysis.summary.ho_default_rate > 0 && (
                        <p className="text-[10px] text-center text-slate-400 mt-2">
                          Non-HO is {((performanceAnalysis.summary.nonho_default_rate / performanceAnalysis.summary.ho_default_rate - 1) * 100).toFixed(0)}% riskier
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Sector Performance with Expandable Sub-Categories */}
              {performanceAnalysis?.sector_analysis && (
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <BarChart3 className="w-5 h-5" />
                          Sector & Industry Performance
                        </CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                          Click any sector to see detailed industry breakdown
                        </p>
                      </div>
                      <Input
                        placeholder="Filter sectors..."
                        value={sectorFilter}
                        onChange={(e) => setSectorFilter(e.target.value)}
                        className="w-48 h-8 text-sm"
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[500px] overflow-auto">
                      {performanceAnalysis.sector_analysis
                        .filter(s => !sectorFilter || s.sector.toLowerCase().includes(sectorFilter.toLowerCase()))
                        .sort((a, b) => b.default_rate - a.default_rate)
                        .map((sector, idx) => {
                          const isExpanded = expandedSectors.has(sector.sector);
                          const subCategories = performanceAnalysis.sub_category_analysis?.filter(
                            sub => sub.sector === sector.sector
                          ).sort((a, b) => b.default_rate - a.default_rate) || [];
                          
                          return (
                            <div key={idx} className="border rounded-lg overflow-hidden">
                              {/* Sector Header - Clickable */}
                              <div 
                                className={`flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                                  sector.risk_indicator === 'HIGH' ? 'bg-red-50 border-l-4 border-l-red-500' :
                                  sector.risk_indicator === 'MEDIUM' ? 'bg-amber-50 border-l-4 border-l-amber-500' :
                                  'bg-green-50 border-l-4 border-l-green-500'
                                }`}
                                onClick={() => {
                                  const newExpanded = new Set(expandedSectors);
                                  if (isExpanded) newExpanded.delete(sector.sector);
                                  else newExpanded.add(sector.sector);
                                  setExpandedSectors(newExpanded);
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                  )}
                                  <div>
                                    <p className="font-semibold">{sector.sector}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {sector.total_loans} loans • {subCategories.length} industries • £{(sector.total_principal / 1000).toFixed(0)}k
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <p className={`text-lg font-bold ${
                                      sector.risk_indicator === 'HIGH' ? 'text-red-600' :
                                      sector.risk_indicator === 'MEDIUM' ? 'text-amber-600' :
                                      'text-green-600'
                                    }`}>
                                      {sector.default_rate}%
                                    </p>
                                    <p className="text-xs text-muted-foreground">{sector.default_loans} defaults</p>
                                  </div>
                                  <div className="text-right text-xs">
                                    <p className="text-blue-600">HO: {sector.ho_loans > 0 ? ((sector.ho_defaults / sector.ho_loans) * 100).toFixed(1) : 0}%</p>
                                    <p className="text-orange-600">Non: {sector.nonho_loans > 0 ? ((sector.nonho_defaults / sector.nonho_loans) * 100).toFixed(1) : 0}%</p>
                                  </div>
                                  <Badge className={
                                    sector.risk_indicator === 'HIGH' ? 'bg-red-600' :
                                    sector.risk_indicator === 'MEDIUM' ? 'bg-amber-500' :
                                    'bg-green-600'
                                  }>
                                    {sector.risk_indicator}
                                  </Badge>
                                </div>
                              </div>
                              
                              {/* Sub-Categories (Expanded) */}
                              {isExpanded && subCategories.length > 0 && (
                                <div className="bg-white border-t">
                                  <div className="px-3 py-2 bg-slate-100 text-xs font-medium text-slate-600">
                                    Industries within {sector.sector}:
                                  </div>
                                  <div className="divide-y">
                                    {subCategories.map((sub, subIdx) => (
                                      <HoverCard key={subIdx} openDelay={100} closeDelay={100}>
                                        <HoverCardTrigger asChild>
                                          <div className="flex items-center justify-between px-6 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                                            <div className="flex items-center gap-2">
                                              <div className={`w-2 h-2 rounded-full ${
                                                sub.default_rate > 15 ? 'bg-red-500' :
                                                sub.default_rate > 8 ? 'bg-amber-500' :
                                                'bg-green-500'
                                              }`} />
                                              <span className="font-medium">{sub.sub_category}</span>
                                              <span className="text-xs text-muted-foreground">({sub.loans} loans)</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                              <span className={`font-bold ${
                                                sub.default_rate > 15 ? 'text-red-600' :
                                                sub.default_rate > 8 ? 'text-amber-600' :
                                                'text-green-600'
                                              }`}>
                                                {sub.default_rate}%
                                              </span>
                                              <span className="text-xs text-red-500">{sub.defaults} defaults</span>
                                            </div>
                                          </div>
                                        </HoverCardTrigger>
                                        <HoverCardContent className="w-80" side="left">
                                          <div className="space-y-2">
                                            <h4 className="font-semibold">{sub.sub_category}</h4>
                                            <p className="text-xs text-muted-foreground">
                                              {sub.loans} loans • {sub.defaults} defaults • £{(sub.principal / 1000).toFixed(0)}k
                                            </p>
                                            {sub.companies?.length > 0 && (
                                              <div className="border-t pt-2">
                                                <p className="text-xs font-medium mb-1">Companies ({sub.companies.length}):</p>
                                                <div className="max-h-32 overflow-auto text-xs space-y-0.5">
                                                  {sub.companies.map((c, i) => (
                                                    <p key={i} className={sub.default_companies?.includes(c) ? 'text-red-600' : ''}>
                                                      {c} {sub.default_companies?.includes(c) && '⚠️'}
                                                    </p>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </HoverCardContent>
                                      </HoverCard>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Seasonality Charts */}
              {performanceAnalysis?.monthly_trends && (
                <div className="grid grid-cols-2 gap-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Funding Volume by Month
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">When do businesses seek funding?</p>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={Object.entries(performanceAnalysis.monthly_trends.funding || {})
                          .map(([month, count]) => ({ month: month.substring(2), count }))
                          .slice(-18)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <RechartsTooltip />
                          <Bar dataKey="count" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        Default Patterns by Month
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">When do defaults occur? (by funding date)</p>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={Object.entries(performanceAnalysis.monthly_trends.defaults || {})
                          .map(([month, count]) => ({ month: month.substring(2), count }))
                          .slice(-18)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <RechartsTooltip />
                          <Bar dataKey="count" fill="#ef4444" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Loan Bracket Risk Analysis */}
              {performanceAnalysis?.loan_bracket_analysis && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <PoundSterling className="w-5 h-5" />
                      Loan Amount Risk Analysis
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">Default rates by loan size, split by HO/Non-HO</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-7 gap-3">
                      {performanceAnalysis.loan_bracket_analysis.map((bracket, idx) => {
                        const hoRate = bracket.ho_total > 0 ? ((bracket.ho_defaults / bracket.ho_total) * 100).toFixed(1) : 0;
                        const nonhoRate = bracket.nonho_total > 0 ? ((bracket.nonho_defaults / bracket.nonho_total) * 100).toFixed(1) : 0;
                        
                        return (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-lg text-center ${
                              bracket.risk_indicator === 'HIGH' ? 'bg-red-50 border border-red-200' :
                              bracket.risk_indicator === 'MEDIUM' ? 'bg-amber-50 border border-amber-200' :
                              'bg-green-50 border border-green-200'
                            }`}
                          >
                            <p className="font-bold text-sm">{bracket.bracket}</p>
                            <p className={`text-xl font-bold ${
                              bracket.risk_indicator === 'HIGH' ? 'text-red-600' :
                              bracket.risk_indicator === 'MEDIUM' ? 'text-amber-600' :
                              'text-green-600'
                            }`}>{bracket.default_rate}%</p>
                            <p className="text-[10px] text-muted-foreground">{bracket.default_loans}/{bracket.total_loans}</p>
                            <div className="mt-2 pt-2 border-t text-[10px] space-y-0.5">
                              <p className="text-blue-600">HO: {hoRate}%</p>
                              <p className="text-orange-600">Non: {nonhoRate}%</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* HO vs Non-HO by Sector */}
              {performanceAnalysis?.ho_analysis && performanceAnalysis?.nonho_analysis && (
                <div className="grid grid-cols-2 gap-6">
                  <Card className="border-blue-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-blue-700">
                        <Home className="w-5 h-5" />
                        Homeowner (HO) by Sector
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {performanceAnalysis.ho_analysis.total_loans} loans • {performanceAnalysis.ho_analysis.default_rate}% default
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1 max-h-[250px] overflow-auto">
                        {performanceAnalysis.ho_analysis.by_sector
                          ?.filter(s => s.loans >= 5)
                          .sort((a, b) => b.default_rate - a.default_rate)
                          .slice(0, 12)
                          .map((s, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1 px-2 rounded hover:bg-blue-50 text-sm">
                              <span className="truncate flex-1">{s.sector}</span>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-muted-foreground">{s.loans}</span>
                                <span className={`font-bold ${s.default_rate > 15 ? 'text-red-600' : s.default_rate > 8 ? 'text-amber-600' : 'text-green-600'}`}>
                                  {s.default_rate}%
                                </span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-orange-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-orange-700">
                        <Building className="w-5 h-5" />
                        Non-Homeowner by Sector
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {performanceAnalysis.nonho_analysis.total_loans} loans • {performanceAnalysis.nonho_analysis.default_rate}% default
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1 max-h-[250px] overflow-auto">
                        {performanceAnalysis.nonho_analysis.by_sector
                          ?.filter(s => s.loans >= 5)
                          .sort((a, b) => b.default_rate - a.default_rate)
                          .slice(0, 12)
                          .map((s, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1 px-2 rounded hover:bg-orange-50 text-sm">
                              <span className="truncate flex-1">{s.sector}</span>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-muted-foreground">{s.loans}</span>
                                <span className={`font-bold ${s.default_rate > 15 ? 'text-red-600' : s.default_rate > 8 ? 'text-amber-600' : 'text-green-600'}`}>
                                  {s.default_rate}%
                                </span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-400 mb-3" />
              <p className="text-muted-foreground">No performance data available</p>
            </div>
          )}
        </TabsContent>

        {/* Policy Editor Tab */}
        {/* Policy Simulation Tab */}
        <TabsContent value="policy" className="mt-6 space-y-6" data-testid="policy-tab">
          {simulationLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
              <p className="text-muted-foreground mt-2">Running simulation...</p>
            </div>
          ) : simulationData ? (
            <>
              {/* Simulation Explanation */}
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-indigo-100 rounded-lg">
                      <SlidersHorizontal className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-indigo-900">Policy Simulation</h3>
                      <p className="text-sm text-indigo-700 mt-1">
                        Model the impact of different affordability limits on bad debt exposure.
                      </p>
                      <div className="mt-3 p-3 bg-white/50 rounded-lg text-sm">
                        <p className="font-medium text-slate-700">Current Policy:</p>
                        <p className="text-slate-600">Everyone can borrow up to <span className="font-bold">100%</span> of monthly sales (1 month)</p>
                        <p className="font-medium text-slate-700 mt-2">Proposed Policy:</p>
                        <p className="text-slate-600">
                          HIGH risk: <span className="text-red-600 font-bold">{customLimits.HIGH}%</span> (2 weeks) • 
                          MEDIUM: <span className="text-amber-600 font-bold">{customLimits.MEDIUM}%</span> (1 month) • 
                          LOW: <span className="text-green-600 font-bold">{customLimits.LOW}%</span> (same as baseline)
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Simulation Controls */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2">
                    <SlidersHorizontal className="w-5 h-5 text-blue-600" />
                    Adjust Affordability Limits
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Change limits and run simulation to see impact on bad debt
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-red-700">HIGH Risk</span>
                        <Badge className="bg-red-600">Default &gt;15%</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={customLimits.HIGH}
                          onChange={(e) => setCustomLimits(prev => ({ ...prev, HIGH: parseInt(e.target.value) || 50 }))}
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground">% of sales</span>
                      </div>
                      <p className="text-xs text-red-600 mt-1">
                        {customLimits.HIGH < 100 ? `Reduces exposure by ${100 - customLimits.HIGH}%` : 
                         customLimits.HIGH > 100 ? `Increases exposure by ${customLimits.HIGH - 100}%` : 
                         'No change'}
                      </p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-amber-700">MEDIUM Risk</span>
                        <Badge className="bg-amber-500">Default 8-15%</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={customLimits.MEDIUM}
                          onChange={(e) => setCustomLimits(prev => ({ ...prev, MEDIUM: parseInt(e.target.value) || 100 }))}
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground">% of sales</span>
                      </div>
                      <p className="text-xs text-amber-600 mt-1">
                        {customLimits.MEDIUM < 100 ? `Reduces exposure by ${100 - customLimits.MEDIUM}%` : 
                         customLimits.MEDIUM > 100 ? `Increases exposure by ${customLimits.MEDIUM - 100}%` : 
                         'No change (baseline)'}
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-green-700">LOW Risk</span>
                        <Badge className="bg-green-600">Default &lt;8%</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={customLimits.LOW}
                          onChange={(e) => setCustomLimits(prev => ({ ...prev, LOW: parseInt(e.target.value) || 200 }))}
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground">% of sales</span>
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        {customLimits.LOW < 100 ? `Reduces exposure by ${100 - customLimits.LOW}%` : 
                         customLimits.LOW > 100 ? `Increases exposure by ${customLimits.LOW - 100}%` : 
                         'No change'}
                      </p>
                    </div>
                  </div>
                  <Button onClick={runCustomSimulation} className="w-full gap-2" data-testid="run-simulation-btn">
                    <RefreshCw className="w-4 h-4" />
                    Run Simulation
                  </Button>
                </CardContent>
              </Card>

              {/* Results Summary */}
              <Card className={`border-2 ${simulationData.summary.reduction > 0 ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    {simulationData.summary.reduction > 0 ? (
                      <TrendingDown className="w-6 h-6 text-green-600" />
                    ) : (
                      <TrendingUp className="w-6 h-6 text-red-600" />
                    )}
                    Simulation Results
                  </CardTitle>
                  {/* Coverage Warning */}
                  {simulationData.coverage && simulationData.coverage.enriched_pct < 100 && (
                    <div className="mt-2 p-2 bg-amber-100 rounded text-xs text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      <span>
                        <strong>{simulationData.coverage.enriched_pct}%</strong> of loans have AI classification • 
                        <strong>£{((simulationData.summary.unenriched_bad_debt || 0) / 1000).toFixed(0)}k</strong> bad debt is unenriched (unchanged in simulation)
                      </span>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-3">
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <p className="text-[10px] text-muted-foreground uppercase">Current Bad Debt</p>
                      <p className="text-xl font-bold text-slate-800">
                        {formatCurrency(simulationData.summary.current_total_bad_debt)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">total exposure</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <p className="text-[10px] text-muted-foreground uppercase">Proposed Bad Debt</p>
                      <p className={`text-xl font-bold ${simulationData.summary.reduction > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(simulationData.summary.proposed_total_bad_debt)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">with policy</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <p className="text-[10px] text-muted-foreground uppercase">Savings</p>
                      <p className={`text-xl font-bold ${simulationData.summary.reduction > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {simulationData.summary.reduction > 0 ? '-' : '+'}
                        {formatCurrency(Math.abs(simulationData.summary.reduction))}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {simulationData.summary.reduction_pct > 0 ? '' : '+'}
                        {Math.abs(simulationData.summary.reduction_pct).toFixed(1)}%
                      </p>
                    </div>
                    <div className="text-center p-3 bg-slate-100 rounded-lg">
                      <p className="text-[10px] text-muted-foreground uppercase">Unenriched</p>
                      <p className="text-xl font-bold text-slate-500">
                        {formatCurrency(simulationData.summary.unenriched_bad_debt || 0)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">no AI data</p>
                    </div>
                    <div className="text-center p-3 bg-indigo-100 rounded-lg">
                      <p className="text-[10px] text-muted-foreground uppercase">Excluded</p>
                      <p className="text-xl font-bold text-indigo-700">
                        {formatCurrency(simulationData.summary.excluded_bad_debt || 0)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">{excludedSubCategories.size} industries</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Impact by Risk Tier */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Impact by Risk Tier (Sub-Category Level)
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Risk is assessed at the sub-category (industry) level, not sector level
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    {['HIGH', 'MEDIUM', 'LOW', 'UNENRICHED'].map(tier => {
                      const tierData = simulationData.by_risk_tier?.[tier];
                      if (!tierData || (tierData.current_bad_debt === 0 && tierData.count === 0)) return null;
                      const isReduction = tierData.change < 0;
                      const count = tierData.count || 0;
                      return (
                        <div 
                          key={tier}
                          className={`p-4 rounded-lg border ${
                            tier === 'HIGH' ? 'bg-red-50 border-red-200' :
                            tier === 'MEDIUM' ? 'bg-amber-50 border-amber-200' :
                            tier === 'LOW' ? 'bg-green-50 border-green-200' :
                            'bg-slate-100 border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <Badge className={
                              tier === 'HIGH' ? 'bg-red-600' :
                              tier === 'MEDIUM' ? 'bg-amber-500' :
                              tier === 'LOW' ? 'bg-green-600' :
                              'bg-slate-400'
                            }>
                              {tier === 'UNENRICHED' ? 'NO AI DATA' : `${tier} RISK`}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{count} {tier === 'UNENRICHED' ? 'defaults' : 'sub-cats'}</span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Current:</span>
                              <span className="font-medium">{formatCurrency(tierData.current_bad_debt)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Proposed:</span>
                              <span className="font-medium">{formatCurrency(tierData.proposed_bad_debt)}</span>
                            </div>
                            <div className={`flex justify-between text-sm pt-2 border-t ${
                              isReduction ? 'text-green-600' : 
                              tierData.change > 0 ? 'text-red-600' : 
                              'text-slate-500'
                            }`}>
                              <span>Change:</span>
                              <span className="font-bold">
                                {tier === 'UNENRICHED' ? 'N/A' : (
                                  <>
                                    {isReduction ? '' : '+'}
                                    {formatCurrency(tierData.change)} ({tierData.change_pct > 0 ? '+' : ''}{tierData.change_pct}%)
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Mixed Risk Sectors Insight */}
              {simulationData.insights?.mixed_risk_sectors > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="w-5 h-5" />
                      Hidden High-Risk Sub-Categories
                    </CardTitle>
                    <p className="text-xs text-amber-700">
                      {simulationData.insights.mixed_risk_sectors} sectors appear safe overall but contain high-risk industries
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {simulationData.insights.mixed_risk_details?.map((detail, idx) => (
                        <div key={idx} className="text-sm text-amber-900 p-2 bg-white/50 rounded">
                          • {detail}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Scenario Builder - Exclude Industries */}
              <Card className="border-2 border-indigo-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Factory className="w-5 h-5 text-indigo-600" />
                        Scenario Builder - Exclude Industries
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        Untick industries to see what bad debt would look like if you didn't support them
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={exportSimulationCSV}
                        className="text-xs gap-1"
                        data-testid="export-simulation-csv"
                      >
                        <FileSpreadsheet className="w-3 h-3" />
                        CSV
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={exportSimulationPDF}
                        className="text-xs gap-1"
                        data-testid="export-simulation-pdf"
                      >
                        <Download className="w-3 h-3" />
                        PDF
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={excludeAllHighRisk}
                        className="text-xs"
                      >
                        Exclude All HIGH Risk
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={clearExclusions}
                        className="text-xs"
                      >
                        Clear All
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={runCustomSimulation}
                        className="text-xs gap-1 bg-indigo-600 hover:bg-indigo-700"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Apply Exclusions
                      </Button>
                    </div>
                  </div>
                  {excludedSubCategories.size > 0 && (
                    <div className="mt-3 p-2 bg-indigo-50 rounded-lg">
                      <p className="text-xs text-indigo-700">
                        <strong>{excludedSubCategories.size}</strong> industries excluded • 
                        Excluded bad debt: <strong>{formatCurrency(simulationData?.summary?.excluded_bad_debt || 0)}</strong>
                      </p>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
                    {/* Group industries by sector */}
                    {(() => {
                      const subCats = (simulationData?.sub_categories || [])
                        .filter(item => item.current_bad_debt > 0);
                      
                      // Group by sector
                      const bySector = {};
                      subCats.forEach(item => {
                        const sector = item.sector || "Unknown";
                        if (!bySector[sector]) {
                          bySector[sector] = {
                            name: sector,
                            industries: [],
                            totalBadDebt: 0,
                            totalDefaults: 0
                          };
                        }
                        bySector[sector].industries.push(item);
                        bySector[sector].totalBadDebt += item.current_bad_debt || 0;
                        bySector[sector].totalDefaults += item.defaults || 0;
                      });
                      
                      // Sort sectors by bad debt descending
                      const sortedSectors = Object.values(bySector)
                        .sort((a, b) => b.totalBadDebt - a.totalBadDebt);
                      
                      return (
                        <div className="divide-y">
                          {sortedSectors.map((sector, sectorIdx) => {
                            const isExpanded = expandedSectors.has(sector.name);
                            const sectorExcludedCount = sector.industries.filter(i => excludedSubCategories.has(i.sub_category)).length;
                            const allExcluded = sectorExcludedCount === sector.industries.length;
                            
                            // Determine dominant risk tier for the sector
                            const riskCounts = { HIGH: 0, MEDIUM: 0, LOW: 0 };
                            sector.industries.forEach(i => {
                              riskCounts[i.risk_tier || 'MEDIUM']++;
                            });
                            const dominantRisk = Object.entries(riskCounts)
                              .sort((a, b) => b[1] - a[1])[0][0];
                            
                            return (
                              <div key={sectorIdx}>
                                {/* Sector Header - Clickable to expand */}
                                <div 
                                  className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                                    allExcluded ? 'bg-red-50' : 'hover:bg-slate-50'
                                  }`}
                                  onClick={() => toggleSectorExpand(sector.name)}
                                >
                                  <div className="flex items-center gap-3">
                                    {isExpanded ? (
                                      <ChevronDown className="w-5 h-5 text-slate-400" />
                                    ) : (
                                      <ChevronRight className="w-5 h-5 text-slate-400" />
                                    )}
                                    <div>
                                      <p className={`font-semibold ${allExcluded ? 'text-red-400 line-through' : ''}`}>
                                        {sector.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {sector.industries.length} industries • {formatCurrency(sector.totalBadDebt)} bad debt
                                        {sectorExcludedCount > 0 && (
                                          <span className="text-red-500 ml-2">
                                            ({sectorExcludedCount} excluded)
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <Badge className={
                                      dominantRisk === 'HIGH' ? 'bg-red-600' :
                                      dominantRisk === 'MEDIUM' ? 'bg-amber-500' :
                                      'bg-green-600'
                                    }>
                                      {dominantRisk}
                                    </Badge>
                                    <span className="text-sm font-medium">
                                      {dominantRisk === 'HIGH' ? customLimits.HIGH :
                                       dominantRisk === 'LOW' ? customLimits.LOW :
                                       customLimits.MEDIUM}%
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Sub-industries - Shown when expanded */}
                                {isExpanded && (
                                  <div className="bg-slate-50/50 border-t">
                                    <div className="px-3 py-1 text-xs font-medium text-slate-500 bg-slate-100">
                                      Industries within {sector.name}:
                                    </div>
                                    <Table>
                                      <TableBody>
                                        {sector.industries
                                          .sort((a, b) => b.current_bad_debt - a.current_bad_debt)
                                          .map((item, idx) => {
                                            const isExcluded = excludedSubCategories.has(item.sub_category);
                                            const fundingLimit = isExcluded ? 0 : 
                                              item.risk_tier === 'HIGH' ? customLimits.HIGH :
                                              item.risk_tier === 'LOW' ? customLimits.LOW : 
                                              customLimits.MEDIUM;
                                            return (
                                              <TableRow 
                                                key={idx} 
                                                className={isExcluded ? 'bg-red-50/50 opacity-60' : 'hover:bg-white'}
                                              >
                                                <TableCell className="w-[50px] pl-8">
                                                  <Checkbox
                                                    checked={!isExcluded}
                                                    onCheckedChange={() => toggleExclusion(item.sub_category)}
                                                    className="data-[state=checked]:bg-green-600"
                                                  />
                                                </TableCell>
                                                <TableCell className={`font-medium ${isExcluded ? 'line-through text-muted-foreground' : ''}`}>
                                                  <div className="flex items-center gap-2">
                                                    <span className={`w-2 h-2 rounded-full ${
                                                      item.risk_tier === 'HIGH' ? 'bg-red-500' :
                                                      item.risk_tier === 'MEDIUM' ? 'bg-amber-500' :
                                                      'bg-green-500'
                                                    }`} />
                                                    {item.sub_category}
                                                  </div>
                                                </TableCell>
                                                <TableCell className="text-center">
                                                  <Badge variant="outline" className={
                                                    item.risk_tier === 'HIGH' ? 'border-red-300 text-red-600' :
                                                    item.risk_tier === 'MEDIUM' ? 'border-amber-300 text-amber-600' :
                                                    'border-green-300 text-green-600'
                                                  }>
                                                    {item.risk_tier}
                                                  </Badge>
                                                </TableCell>
                                                <TableCell className={`text-center font-medium ${isExcluded ? 'text-red-600' : ''}`}>
                                                  {isExcluded ? 'EXCLUDED' : `${fundingLimit}%`}
                                                </TableCell>
                                                <TableCell className="text-right font-medium">
                                                  {formatCurrency(item.current_bad_debt)}
                                                </TableCell>
                                                <TableCell className={`text-right text-sm ${
                                                  isExcluded ? 'text-green-600 font-bold' : 
                                                  item.change < 0 ? 'text-green-600' : 
                                                  item.change > 0 ? 'text-red-600' : ''
                                                }`}>
                                                  {isExcluded ? (
                                                    <span>-{formatCurrency(item.current_bad_debt)}</span>
                                                  ) : (
                                                    <span>{item.change >= 0 ? '+' : ''}{formatCurrency(item.change)}</span>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            );
                                          })}
                                      </TableBody>
                                    </Table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Expand/Collapse buttons */}
                  <div className="flex gap-2 mt-3">
                    <Button variant="ghost" size="sm" onClick={expandAllSectors} className="text-xs">
                      Expand All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={collapseAllSectors} className="text-xs">
                      Collapse All
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Key Insight */}
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-slate-600 mt-0.5" />
                    <div className="text-sm text-slate-700">
                      <p className="font-medium mb-1">How this simulation works:</p>
                      <ul className="list-disc list-inside space-y-1 text-slate-600">
                        <li>Risk is assessed at the <strong>sub-category (industry) level</strong>, not sector level</li>
                        <li>Even a LOW-risk sector can contain HIGH-risk sub-categories (e.g., Event Planning in Entertainment)</li>
                        <li><strong>Untick industries</strong> to see what your bad debt would be if you didn't lend to them at all</li>
                        <li>Changing affordability limits changes your <strong>exposure</strong> (how much you lend)</li>
                        <li>If you halve the limit for HIGH risk (100% → 50%), your bad debt exposure halves</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-400 mb-3" />
              <p className="text-muted-foreground">No simulation data available</p>
              <Button onClick={() => loadSimulation()} className="mt-4">Load Simulation</Button>
            </div>
          )}
        </TabsContent>

        {/* Company Matching Tab */}
        <TabsContent value="matching" className="mt-6" data-testid="matching-tab">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Company Validation Workflow
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Match legal cases with Companies House records to enrich with SIC codes
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cases ({legalCases.length})</SelectItem>
                      <SelectItem value="unmatched">Unmatched ({unmatchedCount})</SelectItem>
                      <SelectItem value="matched">Matched ({matchedCount})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Progress Bar and Auto-Match */}
              {unmatchedCount > 0 && (
                <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm font-medium text-amber-800">
                        {unmatchedCount} cases need Companies House validation
                      </span>
                      <p className="text-xs text-amber-600 mt-0.5">
                        {matchProgress.toFixed(0)}% complete
                      </p>
                    </div>
                    <Button 
                      onClick={handleAutoMatch}
                      disabled={autoMatchLoading}
                      className="gap-2 bg-green-600 hover:bg-green-700"
                      data-testid="auto-match-btn"
                    >
                      {autoMatchLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Auto-Matching...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4" />
                          Auto-Match (95%+ confidence)
                        </>
                      )}
                    </Button>
                  </div>
                  <Progress value={matchProgress} className="h-2" />
                  
                  {autoMatchResults && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <div className="grid grid-cols-4 gap-4 text-center text-sm">
                        <div>
                          <p className="text-green-700 font-bold text-lg">{autoMatchResults.auto_matched}</p>
                          <p className="text-xs text-muted-foreground">Auto-Matched</p>
                        </div>
                        <div>
                          <p className="text-amber-700 font-bold text-lg">{autoMatchResults.low_confidence}</p>
                          <p className="text-xs text-muted-foreground">Low Confidence</p>
                        </div>
                        <div>
                          <p className="text-slate-600 font-bold text-lg">{autoMatchResults.no_match}</p>
                          <p className="text-xs text-muted-foreground">No Match Found</p>
                        </div>
                        <div>
                          <p className="text-blue-700 font-bold text-lg">{autoMatchResults.remaining_unmatched}</p>
                          <p className="text-xs text-muted-foreground">Still Unmatched</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cases Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-[100px]">SF#</TableHead>
                      <TableHead>Company Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Principal</TableHead>
                      <TableHead>CRN</TableHead>
                      <TableHead>SIC Code</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          {filterStatus === "unmatched" 
                            ? "All cases have been matched!" 
                            : "No cases found"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCases.map((c, idx) => (
                        <TableRow key={c.id || idx} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-sm">{c.sf_number}</TableCell>
                          <TableCell className="font-medium max-w-[200px] truncate" title={c.company_name}>
                            {c.company_name}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={
                                c.loan_status?.includes('write') || c.loan_status?.includes('bankrupt')
                                  ? 'border-red-200 text-red-700 bg-red-50'
                                  : 'border-amber-200 text-amber-700 bg-amber-50'
                              }
                            >
                              {c.loan_status || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatCurrency(c.principal)}</TableCell>
                          <TableCell>
                            {c.company_number ? (
                              <span className="font-mono text-xs">{c.company_number}</span>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {c.sic_code ? (
                              <Badge variant="secondary">{c.sic_code}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate text-xs" title={c.industry_description}>
                            {c.industry_description || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            {c.sic_code ? (
                              <Badge className="bg-green-100 text-green-700 gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Matched
                              </Badge>
                            ) : (
                              <Button 
                                size="sm" 
                                onClick={() => openMatchDialog(c)}
                                className="gap-1"
                              >
                                <Search className="w-3 h-3" />
                                Match
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Loan Book Matching Section */}
          <Card className="mt-6">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    Loan Book Company Matching
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Match performing loans with Companies House to enable industry comparison
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {loanBook && (
                    <Badge variant="outline" className="text-sm">
                      {loanBook.matched_count}/{loanBook.total_loans} matched ({((loanBook.matched_count / loanBook.total_loans) * 100).toFixed(0)}%)
                    </Badge>
                  )}
                  <Button 
                    onClick={() => handleLoanBookAutoMatch(0.88)}
                    disabled={loanBookAutoMatchLoading}
                    size="sm"
                    className="gap-2 bg-purple-600 hover:bg-purple-700"
                  >
                    {loanBookAutoMatchLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Matching...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        Auto-Match
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loanBookAutoMatchResults && (
                <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="grid grid-cols-4 gap-4 text-center text-sm">
                    <div>
                      <p className="text-green-700 font-bold text-lg">{loanBookAutoMatchResults.auto_matched}</p>
                      <p className="text-xs text-muted-foreground">Auto-Matched</p>
                    </div>
                    <div>
                      <p className="text-amber-700 font-bold text-lg">{loanBookAutoMatchResults.low_confidence}</p>
                      <p className="text-xs text-muted-foreground">Low Confidence</p>
                    </div>
                    <div>
                      <p className="text-slate-600 font-bold text-lg">{loanBookAutoMatchResults.no_match}</p>
                      <p className="text-xs text-muted-foreground">No Match</p>
                    </div>
                    <div>
                      <p className="text-purple-700 font-bold text-lg">{loanBookAutoMatchResults.remaining_unmatched}</p>
                      <p className="text-xs text-muted-foreground">Still Unmatched</p>
                    </div>
                  </div>
                </div>
              )}
              
              {loanBook && (
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{loanBook.matched_count} matched</span>
                    <span>{loanBook.unmatched_count} remaining</span>
                  </div>
                  <Progress value={(loanBook.matched_count / loanBook.total_loans) * 100} className="h-2" />
                </div>
              )}

              {loanBookCases.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b flex items-center justify-between">
                    <span className="text-sm font-medium">
                      Unmatched Companies ({loanBookCases.filter(c => !c.matched && !c.sic_code).length})
                    </span>
                    <div className="flex items-center gap-2">
                      <Select value={loanBookSortBy} onValueChange={setLoanBookSortBy}>
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="name">Company Name</SelectItem>
                          <SelectItem value="principal">Principal</SelectItem>
                          <SelectItem value="sf_number">SF Number</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setLoanBookSortDir(d => d === "asc" ? "desc" : "asc")}
                        className="h-8 px-2"
                      >
                        {loanBookSortDir === "asc" ? "↑" : "↓"}
                      </Button>
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">SF#</TableHead>
                        <TableHead>Company Name</TableHead>
                        <TableHead>Principal</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loanBookCases
                        .filter(c => !c.matched && !c.sic_code)
                        .sort((a, b) => {
                          const dir = loanBookSortDir === "asc" ? 1 : -1;
                          if (loanBookSortBy === "name") {
                            return ((a.company_name || "").localeCompare(b.company_name || "")) * dir;
                          } else if (loanBookSortBy === "principal") {
                            return ((a.principal || 0) - (b.principal || 0)) * dir;
                          } else {
                            return ((a.sf_number || "").localeCompare(b.sf_number || "")) * dir;
                          }
                        })
                        .slice(0, 50)
                        .map((loan, idx) => (
                          <TableRow key={loan.id || idx}>
                            <TableCell className="font-mono text-xs">{loan.sf_number}</TableCell>
                            <TableCell className="font-medium">{loan.company_name || "-"}</TableCell>
                            <TableCell>{formatCurrency(loan.principal)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{loan.loan_status}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button 
                                size="sm" 
                                onClick={() => {
                                  setSelectedLoanBookCase(loan);
                                  setLoanBookMatchDialogOpen(true);
                                }}
                                className="gap-1"
                              >
                                <Search className="w-3 h-3" />
                                Match
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  {loanBookCases.filter(c => !c.matched && !c.sic_code).length > 50 && (
                    <div className="px-4 py-2 bg-slate-50 border-t text-center text-sm text-muted-foreground">
                      Showing first 50 of {loanBookCases.filter(c => !c.matched && !c.sic_code).length} unmatched companies
                    </div>
                  )}
                </div>
              )}
              
              {loanBook && loanBook.unmatched_count === 0 && (
                <div className="text-center py-6">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-2" />
                  <p className="text-green-700 font-medium">All loan book companies have been matched!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Loan Book Match Dialog */}
          <Dialog open={loanBookMatchDialogOpen} onOpenChange={setLoanBookMatchDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Match Company: {selectedLoanBookCase?.company_name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="p-3 bg-purple-50 rounded-lg text-sm border border-purple-200">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-muted-foreground">SF#:</span> <span className="font-medium">{selectedLoanBookCase?.sf_number || '-'}</span></div>
                    <div><span className="text-muted-foreground">Principal:</span> <span className="font-medium">{formatCurrency(selectedLoanBookCase?.principal)}</span></div>
                    <div><span className="text-muted-foreground">Status:</span> <span className="font-medium">{selectedLoanBookCase?.loan_status || '-'}</span></div>
                    <div><span className="text-muted-foreground">Product:</span> <span className="font-medium">{selectedLoanBookCase?.ho_status || '-'}</span></div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Input 
                    placeholder="Search company name..."
                    defaultValue={selectedLoanBookCase?.company_name}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearchCompany(e.target.value);
                    }}
                    id="loanbook-company-search-input"
                  />
                  <Button 
                    onClick={() => {
                      const input = document.getElementById('loanbook-company-search-input');
                      handleSearchCompany(input?.value || selectedLoanBookCase?.company_name);
                    }}
                    disabled={searchLoading}
                  >
                    {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                  {searchLoading ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      <p className="text-sm text-muted-foreground mt-2">Searching Companies House...</p>
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Search for a company to see results</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Match</TableHead>
                          <TableHead>Company Name</TableHead>
                          <TableHead>CRN</TableHead>
                          <TableHead>SIC Code</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {searchResults.map((result, idx) => {
                          const score = result.match_score || result.score || 0;
                          return (
                            <TableRow key={idx} className="hover:bg-slate-50">
                              <TableCell>
                                <Badge variant={score >= 0.8 ? "default" : score >= 0.5 ? "secondary" : "outline"}>
                                  {(score * 100).toFixed(0)}%
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium">{result.company_name || result.title}</TableCell>
                              <TableCell className="font-mono text-sm">{result.company_number}</TableCell>
                              <TableCell>
                                {(result.sic_codes?.[0] || result.sic_code) && (
                                  <Badge variant="outline" className="text-xs">
                                    {result.sic_codes?.[0] || result.sic_code}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button 
                                  size="sm" 
                                  onClick={() => saveLoanBookMatch(result.company_number)}
                                  disabled={savingMatch}
                                  className="gap-1"
                                >
                                  {savingMatch ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Check className="w-3 h-3" />
                                  )}
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Homeowner Tab */}
        <TabsContent value="homeowner" className="mt-6" data-testid="homeowner-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5 text-blue-600" />
                Homeowner vs Non-Homeowner Analysis
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Compare default rates between HO and NONHO secured loans
              </p>
            </CardHeader>
            <CardContent>
              {homeownerAnalysis ? (
                <div className="space-y-6">
                  {/* HO Comparison Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    {homeownerAnalysis.ho_comparison?.comparison
                      ?.filter(item => item.total_count >= 5)
                      ?.map((item, idx) => {
                      const isHighRisk = item.default_rate > 10;
                      return (
                        <Card key={idx} className={`${
                          isHighRisk ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
                        }`}>
                          <CardContent className="pt-6 text-center">
                            <Badge className={`mb-3 ${
                              item.homeowner_status === 'HO' ? 'bg-blue-100 text-blue-700' :
                              item.homeowner_status === 'NONHO' ? 'bg-orange-100 text-orange-700' :
                              'bg-slate-100'
                            }`}>
                              {item.homeowner_status === 'HO' ? 'Homeowner' : 
                               item.homeowner_status === 'NONHO' ? 'Non-Homeowner' : 'Unknown'}
                            </Badge>
                            <p className={`text-4xl font-bold ${isHighRisk ? 'text-red-700' : 'text-green-700'}`}>
                              {Math.min(item.default_rate, 100)}%
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">Default Rate</p>
                            <div className="mt-4 pt-4 border-t text-sm">
                              <span className="text-red-600 font-medium">{item.legal_count}</span> legal / {item.total_count} total
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Insight */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                      <Info className="w-4 h-4" />
                      Credit Policy Insight
                    </h4>
                    <p className="text-blue-700">
                      {homeownerAnalysis.ho_comparison?.insight}
                    </p>
                  </div>

                  {/* Book Composition */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Loan Book Composition</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {homeownerAnalysis.loan_book_ho_breakdown?.by_homeowner_status?.filter(h => h.homeowner_status !== 'Unknown').map((ho, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                            <span className="flex items-center gap-2">
                              {ho.homeowner_status === 'HO' ? (
                                <Home className="w-4 h-4 text-blue-500" />
                              ) : (
                                <Building2 className="w-4 h-4 text-orange-500" />
                              )}
                              {ho.homeowner_status === 'HO' ? 'Homeowner' : 'Non-Homeowner'}
                            </span>
                            <span className="font-medium">{ho.count} ({ho.count_pct}%)</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Principal by Segment</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {homeownerAnalysis.loan_book_ho_breakdown?.by_homeowner_status?.filter(h => h.homeowner_status !== 'Unknown').map((ho, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                            <span>{ho.homeowner_status === 'HO' ? 'Homeowner' : 'Non-Homeowner'}</span>
                            <span className="font-medium">{formatCurrency(ho.total_principal)}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">Loading homeowner analysis...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Company Match Dialog */}
      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Match Company: {selectedCase?.company_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">SF#:</span> {selectedCase?.sf_number}</div>
                <div><span className="text-muted-foreground">Principal:</span> {formatCurrency(selectedCase?.principal)}</div>
                <div><span className="text-muted-foreground">Status:</span> {selectedCase?.loan_status}</div>
                <div><span className="text-muted-foreground">Balance:</span> {formatCurrency(selectedCase?.balance)}</div>
              </div>
            </div>

            <div className="flex gap-2">
              <Input 
                placeholder="Search company name..."
                defaultValue={selectedCase?.company_name}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchCompany(e.target.value);
                }}
                id="company-search-input"
              />
              <Button 
                onClick={() => {
                  const input = document.getElementById('company-search-input');
                  handleSearchCompany(input?.value);
                }}
                disabled={searchLoading}
              >
                {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              {searchLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  <p className="text-sm text-muted-foreground mt-2">Searching Companies House...</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Search for a company to see results</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Match</TableHead>
                      <TableHead>Company Name</TableHead>
                      <TableHead>CRN</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.map((result, idx) => (
                      <TableRow key={idx} className="hover:bg-slate-50">
                        <TableCell>
                          <Badge variant={result.match_score >= 0.8 ? "default" : result.match_score >= 0.5 ? "secondary" : "outline"}>
                            {(result.match_score * 100).toFixed(0)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{result.company_name}</TableCell>
                        <TableCell className="font-mono text-sm">{result.company_number}</TableCell>
                        <TableCell>
                          <Badge variant={result.company_status === 'active' ? 'outline' : 'destructive'}>
                            {result.company_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            onClick={() => handleSaveMatch(result.company_number, result)}
                            disabled={savingMatch}
                            className="gap-1"
                          >
                            {savingMatch ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )}
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-2">Can't find the company? Enter CRN manually:</p>
              <div className="flex gap-2">
                <Input 
                  placeholder="Enter company number (e.g., 12345678)"
                  id="manual-crn-input"
                  className="font-mono"
                />
                <Button 
                  variant="outline"
                  onClick={() => {
                    const input = document.getElementById('manual-crn-input');
                    if (input?.value) handleSaveMatch(input.value, { company_name: 'Manual Entry' });
                  }}
                  disabled={savingMatch}
                >
                  Save CRN
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
